/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Dialog } from 'components/dialogs';
import { Button, Divider, Icon, Classes } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { SavedStatesActionCreator } from 'hooks/savedViews/actions';
import { transformExplorerCardToExplorer } from 'hooks/savedViews/helpers';
import { useSavedStateSelectors } from 'hooks/savedViews/hooks';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { ColorScalesContext } from 'views/common/colorScalesContext';
import { Header } from 'views/savedViews/header';
import { useHistory } from 'react-router-dom';
import { ManageViewsStateContext, ManageViewsActionCreator } from './useManageViewsState';
import { MetaDataSideBar } from 'views/savedViews/metaDataSideBar';
import { useDefaultColors } from 'utilities/setUpColorScale';
import { Filters } from 'views/filters';
import { Workspace } from 'views/workspace';
import { MiniExplorer } from 'views/savedViews/miniExplorer';
import { TimelineWrapper } from '/views/timeline/timelineWrapper';
import { FilterHub } from 'views/filterHub';
import { AddDescriptionDialog } from 'views/savedViews/dialogs/addDescriptionDialog';

export function RenderWidget({ widget: formattedWidget, toggleFavorite, updateUrlPathCallback = undefined }) {
	const { state: { explorer: { layout }}, dispatch } = useContext(WorkspaceContext);
	const { dispatch: savedStatesDispatch } = useSavedStateSelectors();
	const { 
		state: { isEditing }, 
		dispatch: manageViewsStateDispatch 
	} = useContext(ManageViewsStateContext);
	const history = useHistory();
	const [isMetaDataOpen, setIsMetaDataOpen] = useState(true);
	const [isDeleteWidgetModalOpen, setDeleteWidgetModalOpen] = useState(false);
	const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
	const [isAddDescriptionModalOpen, setIsAddDescriptionModalOpen] = useState(false);

	const openDeleteModal = () => setDeleteWidgetModalOpen(true);
	const closeDeleteModal = () => setDeleteWidgetModalOpen(false);
	const openWarningDialog = () => setIsWarningDialogOpen(true);
	const closeWarningDialog = () => setIsWarningDialogOpen(false);
	const toggleDescriptionModal = () => setIsAddDescriptionModalOpen(state => !state);
	
	// Covert the saved widget object into a dashboard object
	const widget = useMemo(() => {
		if (formattedWidget) {
			return formattedWidget;
		} else {
			return null;
		}
	}, [formattedWidget]);

	const [name, setName] = useState(widget.metaData.name);
	const [description, setDescription] = useState(widget.metaData?.description || '');

	const appSpecificColors = useContext(ColorScalesContext);
	const getColorScale = useDefaultColors(appSpecificColors);

	// Open view in explorer
	const loadExplorer = useCallback(() => {
		const explorer = transformExplorerCardToExplorer(formattedWidget);
		dispatch(WorkspaceActionCreator.loadSavedView('explorer', explorer));

		const path = updateUrlPathCallback
			? updateUrlPathCallback(explorer)
			: '/explorer';

		history.push(path);
		
	}, [formattedWidget, dispatch, updateUrlPathCallback, history]);

	// Delete the view
	const handleDelete = useCallback(() => {
		manageViewsStateDispatch(ManageViewsActionCreator.setSelectedView(null));
		savedStatesDispatch(
			SavedStatesActionCreator.deleteView(
				'widgets',
				formattedWidget.metaData.id
			)
		);
	}, [formattedWidget.metaData.id, savedStatesDispatch, manageViewsStateDispatch]);

	const handleCancel = useCallback(() => {
		manageViewsStateDispatch(ManageViewsActionCreator.stopEditing());
		manageViewsStateDispatch(ManageViewsActionCreator.setSelectedView(null));
		setName(widget?.metaData?.name);
		setDescription(widget?.metaData?.description);
	}, [manageViewsStateDispatch, widget]);

	const handleSave = useCallback(() => {
		if (name === '') {
			openWarningDialog();
			return;
		}
		
		const updatedView = {
			...widget,
			metaData: {
				...widget.metaData,
				name: name,
				description: description
			},
			dataConfiguration: {
				...widget.dataConfiguration,
				endpointQuery: {
					...layout.cards[0].endpointQuery 
				}
			},
			widgetConfiguration: {
				...layout.cards[0].widgetConfiguration
			},
			type: layout.cards[0].type
		}
		
		savedStatesDispatch(SavedStatesActionCreator.updateView(updatedView));
		manageViewsStateDispatch(ManageViewsActionCreator.stopEditing());
	}, [name, widget, description, layout, savedStatesDispatch, manageViewsStateDispatch]);

	const handleEdit = () => {
		manageViewsStateDispatch(ManageViewsActionCreator.startEditing());
	}

	useEffect(() => {
		const explorer = transformExplorerCardToExplorer(formattedWidget);
		dispatch(WorkspaceActionCreator.loadSavedView('explorer', explorer));
	}, [dispatch, formattedWidget]);

	const handleAddDescription = (newDescription) => {
		setDescription(newDescription);
		toggleDescriptionModal();
	};

	return (
		<ColorScalesContext.Provider value={getColorScale}>
			<Fragment>
				<Header
					viewName={name}
					onOpen={!isEditing && loadExplorer}
					onDelete={!isEditing && openDeleteModal}
					onEdit={!isEditing && handleEdit}
					onCancel={isEditing && handleCancel}
					onSave={isEditing && handleSave}
					onAddDescription={isEditing && toggleDescriptionModal}
					onRename={setName}
					toggleFavorite={toggleFavorite}
					isFavorite={widget.metaData.isFavorite}
				/>
				{isDeleteWidgetModalOpen &&
					<Dialog style={styles.Dialog} 
						title={"Delete Widget"} 
						body = {
							<div className={Classes.DIALOG_BODY}>
								<p>Are you sure you want to delete this view? It cannot be undone.</p> 
							</div> 
						}
						icon={<Icon icon={IconNames.TRASH} intent={"danger"}/>}
						onClose={closeDeleteModal} 
						footer = {
							<div className={Classes.DIALOG_FOOTER} >
								<Button minimal={true} css={css `.bp3-button-text {color: #5c7080;}`} onClick={closeDeleteModal}>Cancel</Button> 
								<Button outlined={true} onClick={handleDelete} intent="primary">Delete</Button>
							</div>
						}
					/>
				}
				{isWarningDialogOpen && 
					<SaveWarningDialog onClose={closeWarningDialog} />
				}
				{isAddDescriptionModalOpen &&
					<AddDescriptionDialog 
						initialDescription={description} 
						closeDialog={toggleDescriptionModal} 
						handleSubmit={handleAddDescription} 
					/>
				}
				<Divider css={css`margin-left: -20px; margin-right: -20px; padding-top: 10px;`}/>
				<div css={[viewWrapper, css`right: ${isMetaDataOpen ? '350px' : '1px'};`]}>
					<div css={DashboardCardWrapper}>
						<Workspace isReady={layout !== null}>
							<div css={explorerStyle}>
								<div css={headerCardStyle}>
									<Filters />
								</div>

								<div css={layoutStyle}>
									<MiniExplorer hideHeader={!isEditing}/>
								</div>
								<div css={css`visibility: hidden; width: 0px;`}>
									<TimelineWrapper />
									<FilterHub />
								</div>
							</div>
						</Workspace>
					</div>
					<Button 
						icon={isMetaDataOpen ? IconNames.CHEVRON_RIGHT : IconNames.CHEVRON_LEFT} 
						css={collapseButton}
						onClick={() => setIsMetaDataOpen(state => !state)}
						open={isMetaDataOpen}
					/>
					{isMetaDataOpen &&
						<MetaDataSideBar 
							description={description} 
							cssStyles={metaDataCss}
							selectedDefinitionName={widget.dataConfiguration.selectedDefinition}
							facets={widget.dataConfiguration.filters}
							timeline={widget.dataConfiguration.filters?.timeline}
						/>
					}
				</div>
			</Fragment>
		</ColorScalesContext.Provider>
	);
}

const SaveWarningDialog = ({ onClose }) => {
	return (
		<Dialog style={styles.Dialog} 
			title = {"Add View Title"}
			body = { 
				<div className={Classes.DIALOG_BODY}>
					<p>Before we can save your view, add a title.</p> 
				</div> 
			}
			icon={<Icon icon={IconNames.MANUALLY_ENTERED_DATA} intent={"none"}/>}
			onClose={onClose} 
			footer = {
				<div className={Classes.DIALOG_FOOTER} >
					<Button 
						outlined={true} 
						css={css `padding-left: 15px; padding-right: 15px;`} 
						intent="primary" 
						onClick={onClose}>
							OK
					</Button>
				</div>
			}
		/>
	)
}

const metaDataCss = css`
	margin-top: 20px;
	margin-bottom: 20px;
`;

const collapseButton = css`
	margin-top: 20px;
	margin-bottom: 20px;
	min-width: 0;
`;

const viewWrapper = css`
	overflow: hidden;
	height: 100%;
	display: flex;
	flex-direction: row;
	justify-content: stretch;
	padding: 10px;
	box-shadow: rgb(0 0 0 / 10%) 0 12px 48px 0;
`;

const DashboardCardWrapper = css`
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-template-rows: repeat(1, 1fr);
  grid-column-gap: 20px;
  grid-row-gap: 20px;
  overflow: hidden;
  padding: 20px;
`;

const styles = { 
	Dialog: css`
	.bp3-button{ 
		margin-left: 5px; 
	}

	.bp3-dialog-footer{ 
		text-align: right; 
	}

	background: white; 
`,	
}

const explorerStyle = css`
	display: flex;
	overflow: hidden;
`;

const cards = css`
	display: flex;
	align-items: center;
`;

const tightCard = css`
	${cards};
	padding: 0;
	overflow: hidden;
`;

const headerCardStyle = css`
	visibility: hidden;
	height: 0px;
	width: 0px;
`;

const layoutStyle = css`
	${tightCard};
	flex: 1 1 auto;
`;